import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { useRemote } from './useRemote';
import { formatMoney } from '../mirror/time';
import { capFor, fetchIndex, fetchReport, isRenderable } from '../analytics';
import type {
  BookingsReport,
  GuestsReport,
  LoadedReport,
  ReportIndexEntry,
  RevenueReport,
  ServicesReport,
  UtilizationReport,
} from '../analytics';
import { useLocale, useT } from '../messages';
import type { Messages, T, UiLocale } from '../messages';

/**
 * The analytics section — `reporting-api`, the read replica (dev.md §5).
 *
 * ## The index drives the menu
 *
 * `GET /v1/reports` exists "precisely so clients discover the set", so the tabs
 * below are whatever the index returned and the window control is bounded by the
 * `max_window_days` it published. Nothing about the five reports is hard-coded
 * here except the *tables* — a report the service grows before this bundle does
 * is listed and labelled rather than crashing the section, and a cap that moves
 * on the server moves here on the next load. A cap copied into a bundle drifts,
 * and the first anybody hears of it is a `400`.
 *
 * ## Why it is a section and not a tab
 *
 * The Reports tab already exists and is the owner's *ledger* — six folds
 * computed by `core-api`, snapshotted into IndexedDB, readable offline with
 * their age stated (`mirror/reports.ts`). This is a different thing on the same
 * activity: five deeper cuts, from a service that reads a replica and cannot be
 * mirrored honestly (windows are chosen at read time; a snapshot of "the last 30
 * days" is a snapshot of a *different* thirty days by tomorrow). Putting it
 * below the ledger keeps one place an owner sits down with, and keeps the two
 * mechanisms visibly different: one says when it last synced, the other refuses
 * to pretend it has anything when the replica is unreachable.
 *
 * ## Revenue is the one whose caps differ by grain
 *
 * 400 days by day, 1 100 by week, 1 830 by month — because the cost is in the
 * number of buckets drawn rather than in the span. `capFor` reads whichever
 * applies, so the control cannot offer a span the service will refuse.
 */

/**
 * The five the index publishes today, named in our own words.
 *
 * Translated at render, and **keyed on the report id rather than replaced by
 * it**: a report the service grows before this bundle does falls through to its
 * raw id, which is the same graceful degradation the tables have.
 */
const LABELS: Record<string, keyof Messages> = {
  'reports.revenue': 'analytics.labelRevenue',
  'reports.bookings': 'analytics.labelBookings',
  'reports.utilization': 'analytics.labelUtilization',
  'reports.services': 'analytics.labelServices',
  'reports.guests': 'analytics.labelGuests',
};

function labelFor(t: T, id: string): string {
  const key = LABELS[id];
  return key === undefined ? id : t(key as 'analytics.labelRevenue');
}

/** Grain is meaningful for exactly two of the five; the rest ignore it. */
const GRAINED = new Set(['reports.revenue', 'reports.guests']);

export function Analytics(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  const currency = session?.tenant.currency ?? 'USD';

  // The index is asked for once per mount. It is a route table, not a number
  // that moves during a sitting.
  const index = useRemote((signal) => fetchIndex(runtime.reportingApi, signal), [], {
    pollMs: 0,
  });

  const entries = index.data?.reports ?? [];
  const [pickedId, setPickedId] = useState<string | null>(null);
  const entry: ReportIndexEntry | null =
    entries.find((row) => row.id === pickedId) ?? entries[0] ?? null;

  const [grain, setGrain] = useState<'day' | 'week' | 'month'>('month');
  const [days, setDays] = useState(90);

  const usesGrain = entry !== null && GRAINED.has(entry.id);
  const cap = entry === null ? null : capFor(entry, usesGrain ? grain : undefined);
  // Never ask for more than the service publishes. It would clamp `?days` and
  // say so, but offering a control that silently means something else is the
  // kind of small lie a dashboard is built out of.
  const asked = cap === null ? days : Math.min(days, cap);

  const report = useRemote(
    (signal) =>
      entry === null
        ? Promise.reject(new Error('no report selected'))
        : fetchReport(
            runtime.reportingApi,
            entry,
            { days: asked, ...(usesGrain ? { grain } : {}) },
            signal,
          ),
    [entry?.id ?? '', asked, usesGrain ? grain : ''],
    { pollMs: 0, enabled: entry !== null },
  );

  if (index.error !== null && index.data === null) {
    // No reporting service behind this origin (dev runs without the replica
    // it reads). The owner has nothing to act on here, so the card is simply
    // absent; the routing diagnostic goes to the console for whoever does.
    console.info(
      `deeper reports hidden: reporting service did not answer (${index.error.message}); VITE_REPORTING_API_URL decides where it looks`,
    );
    return null;
  }

  if (entry === null) {
    return (
      <article className="panel">
        <h2>{t('analytics.heading')}</h2>
        <p className="muted">
          {index.loading ? t('analytics.asking') : t('analytics.nonePublished')}
        </p>
      </article>
    );
  }

  return (
    <article className="panel view--analytics">
      <h2>{t('analytics.heading')}</h2>

      <nav className="report-tabs" aria-label={t('analytics.navLabel')}>
        {entries.map((row) => (
          <button
            key={row.id}
            type="button"
            className={`tabs__tab${row.id === entry.id ? ' is-current' : ''}`}
            aria-current={row.id === entry.id ? 'true' : undefined}
            onClick={() => setPickedId(row.id)}
          >
            {labelFor(t, row.id)}
          </button>
        ))}
      </nav>

      {/* The service's own one-line description of the report. Domain C. */}
      <p className="muted">{entry.summary}</p>

      <div className="row-actions report-window">
        <label className="field field--row">
          <span>{t('analytics.last')}</span>
          <input
            type="number"
            min={1}
            max={cap ?? 4000}
            value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
          />
          <span>{t('analytics.days')}</span>
        </label>
        {usesGrain ? (
          <label className="field field--row">
            <span>{t('analytics.by')}</span>
            <select
              value={grain}
              onChange={(e) => setGrain(e.target.value as 'day' | 'week' | 'month')}
            >
              <option value="day">{t('analytics.grainDay')}</option>
              <option value="week">{t('analytics.grainWeek')}</option>
              <option value="month">{t('analytics.grainMonth')}</option>
            </select>
          </label>
        ) : null}
        {cap === null ? null : (
          <small className="muted">
            {t('analytics.cap', { cap })}
            {asked !== days ? t('analytics.capAsking', { asked }) : ''}.
          </small>
        )}
      </div>

      {!isRenderable(entry.id) ? (
        <p className="muted">
          {t('analytics.noTable', { id: entry.id })} <code>{entry.path}</code>.
        </p>
      ) : report.data === null ? (
        <p className="muted">
          {report.error === null
            ? t('analytics.reading')
            : t('analytics.couldNotRead', { message: report.error.message })}
        </p>
      ) : (
        <>
          {report.error === null ? null : (
            <p className="error" role="alert">
              {report.error.message}
            </p>
          )}
          <ReportTable loaded={report.data} currency={currency} t={t} locale={locale} />
        </>
      )}
    </article>
  );
}

function ReportTable({
  loaded,
  currency,
  t,
  locale,
}: {
  loaded: LoadedReport;
  currency: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  switch (loaded.id) {
    case 'reports.revenue':
      return <Revenue report={loaded.body} currency={currency} t={t} locale={locale} />;
    case 'reports.bookings':
      return <Bookings report={loaded.body} t={t} />;
    case 'reports.utilization':
      return <Utilization report={loaded.body} t={t} />;
    case 'reports.services':
      return <Services report={loaded.body} currency={currency} t={t} locale={locale} />;
    case 'reports.guests':
      return <Guests report={loaded.body} currency={currency} t={t} locale={locale} />;
  }
}

function WindowLine({
  from,
  to,
  clamped,
  t,
}: {
  from: string;
  to: string;
  clamped: boolean;
  t: T;
}): ReactNode {
  return (
    <small className="muted">
      {t('analytics.window', { from, to })}
      {clamped ? t('analytics.clamped') : ''}
    </small>
  );
}

/* ------------------------------------------------------------------ */
/* Revenue                                                             */
/* ------------------------------------------------------------------ */

function Revenue({
  report,
  currency,
  t,
  locale,
}: {
  report: RevenueReport;
  currency: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  return (
    <>
      <WindowLine from={report.date_from} to={report.date_to} clamped={report.clamped} t={t} />
      <div className="table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              {/* `grain` is the service's own word for the bucket size. */}
              <th scope="col">{report.grain}</th>
              <th scope="col">{t('reports.colCounter')}</th>
              <th scope="col">{t('reports.colDeposits')}</th>
              <th scope="col">{t('reports.colCertificates')}</th>
              <th scope="col">{t('reports.colCreditTopups')}</th>
              <th scope="col">{t('reports.colGivenAway')}</th>
              <th scope="col">{t('reports.colTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {report.buckets.map((bucket) => (
              <tr key={bucket.bucket}>
                <th scope="row">{bucket.bucket}</th>
                <td>{formatMoney(bucket.by_source.settlement_cents, currency, locale)}</td>
                <td>{formatMoney(bucket.by_source.deposit_cents, currency, locale)}</td>
                <td>{formatMoney(bucket.by_source.certificate_cents, currency, locale)}</td>
                <td>{formatMoney(bucket.by_source.cash_credit_topup_cents, currency, locale)}</td>
                <td>{formatMoney(bucket.discount_cents, currency, locale)}</td>
                <td>
                  <strong>{formatMoney(bucket.total_cents, currency, locale)}</strong>
                </td>
              </tr>
            ))}
            {report.buckets.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted">
                  {t('analytics.noMoney')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/*
        The credit block, beside the revenue and never inside it. The field names
        carry §7.4 rule 2 and so does this table: two of these four are income
        and two never are, and a legend that collapsed them would overstate the
        year by exactly the goodwill the shop gave away.
      */}
      <h3>{t('analytics.credit')}</h3>
      <div className="tiles">
        <Tile
          label={t('analytics.cashTopUps')}
          value={formatMoney(report.credit.cash_top_up_cents, currency, locale)}
        />
        <Tile
          label={t('analytics.cashSpent')}
          value={formatMoney(report.credit.cash_spent_cents, currency, locale)}
        />
        <Tile
          label={t('analytics.giftIssued')}
          value={formatMoney(report.credit.gift_issued_cents, currency, locale)}
        />
        <Tile
          label={t('analytics.giftSpent')}
          value={formatMoney(report.credit.gift_spent_cents, currency, locale)}
        />
      </div>
      <p className="muted">
        {t('analytics.recognized', {
          total: formatMoney(report.total_cents, currency, locale),
          count: report.settlements,
        })}
      </p>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Bookings                                                            */
/* ------------------------------------------------------------------ */

function Bookings({ report, t }: { report: BookingsReport; t: T }): ReactNode {
  return (
    <>
      <WindowLine from={report.date_from} to={report.date_to} clamped={report.clamped} t={t} />
      <div className="table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th scope="col">{t('analytics.colSource')}</th>
              <th scope="col">{t('analytics.colBookings')}</th>
              <th scope="col">{t('analytics.colCovers')}</th>
              <th scope="col">{t('analytics.colCancelled')}</th>
              <th scope="col">{t('analytics.colNoShows')}</th>
              <th scope="col">{t('analytics.colMedianLead')}</th>
              <th scope="col">{t('analytics.colSample')}</th>
            </tr>
          </thead>
          <tbody>
            {report.by_source.map((row) => (
              <tr key={row.source}>
                {/* The booking source, in the wire's own vocabulary. */}
                <th scope="row">{row.source}</th>
                <td>{row.bookings}</td>
                <td>{row.covers}</td>
                <td>{row.cancellations}</td>
                <td>{row.no_shows}</td>
                <td>
                  {row.lead_time.median_hours === null
                    ? '—'
                    : t('analytics.leadHours', { hours: row.lead_time.median_hours })}
                </td>
                {/*
                  The sample is a column rather than a footnote: a median over
                  four bookings is not the same fact as a median over four
                  thousand, and a table that cannot tell them apart will
                  eventually be believed about the wrong one.
                */}
                <td className="muted">{row.lead_time.sample}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{t('analytics.rowAll')}</th>
              <td>{report.bookings}</td>
              <td>{report.covers}</td>
              <td>{report.cancellations}</td>
              <td>{report.no_shows}</td>
              <td>
                {report.lead_time.median_hours === null
                  ? '—'
                  : t('analytics.leadHours', { hours: report.lead_time.median_hours })}
              </td>
              <td className="muted">{report.lead_time.sample}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Utilization                                                         */
/* ------------------------------------------------------------------ */

function Utilization({ report, t }: { report: UtilizationReport; t: T }): ReactNode {
  return (
    <>
      <WindowLine from={report.date_from} to={report.date_to} clamped={report.clamped} t={t} />
      <div className="table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th scope="col">{t('analytics.colResource')}</th>
              <th scope="col">{t('analytics.colLanes')}</th>
              <th scope="col">{t('analytics.colOpenHours')}</th>
              <th scope="col">{t('analytics.colSellableHours')}</th>
              <th scope="col">{t('analytics.colOccupiedHours')}</th>
              <th scope="col">{t('analytics.colUsed')}</th>
              <th scope="col">{t('analytics.colBookings')}</th>
            </tr>
          </thead>
          <tbody>
            {report.resources.map((row) => (
              <tr key={row.resource_id}>
                <th scope="row">{row.name}</th>
                {/* A pool reports once, with its lane count (spec §4.6). */}
                <td>{row.units}</td>
                <td>{hours(row.open_minutes)}</td>
                <td>{hours(row.capacity_minutes)}</td>
                <td>{hours(row.occupied_minutes)}</td>
                <td>{row.utilization === null ? '—' : `${Math.round(row.utilization * 100)}%`}</td>
                <td>{row.bookings}</td>
              </tr>
            ))}
            {report.resources.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted">
                  {t('analytics.nothingOpen')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

function hours(minutes: number): string {
  return String(Math.round(minutes / 60));
}

/* ------------------------------------------------------------------ */
/* Services                                                            */
/* ------------------------------------------------------------------ */

function Services({
  report,
  currency,
  t,
  locale,
}: {
  report: ServicesReport;
  currency: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  return (
    <>
      <WindowLine from={report.date_from} to={report.date_to} clamped={report.clamped} t={t} />
      <div className="table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th scope="col">{t('reports.colItem')}</th>
              <th scope="col">{t('reports.colSold')}</th>
              <th scope="col">{t('reports.colAtList')}</th>
              <th scope="col">{t('reports.colGivenAway')}</th>
              <th scope="col">{t('reports.colNet')}</th>
              <th scope="col">{t('analytics.colBooked')}</th>
              <th scope="col">{t('analytics.colAvgParty')}</th>
            </tr>
          </thead>
          <tbody>
            {report.items.map((item) => (
              <tr key={item.item_id ?? item.name}>
                <th scope="row">{item.name}</th>
                <td>{item.qty}</td>
                <td>{formatMoney(item.list_cents, currency, locale)}</td>
                <td>{formatMoney(item.discount_cents, currency, locale)}</td>
                <td>{formatMoney(item.net_cents, currency, locale)}</td>
                <td>{item.bookings}</td>
                <td>{item.average_party_size === null ? '—' : item.average_party_size}</td>
              </tr>
            ))}
            {report.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted">
                  {t('reports.nothingSold')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {report.locale === null ? null : (
        <small className="muted">{t('analytics.namesIn', { locale: report.locale })}</small>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Guests                                                              */
/* ------------------------------------------------------------------ */

function Guests({
  report,
  currency,
  t,
  locale,
}: {
  report: GuestsReport;
  currency: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  return (
    <>
      <WindowLine from={report.date_from} to={report.date_to} clamped={report.clamped} t={t} />
      <div className="tiles">
        <Tile label={t('analytics.tileGuests')} value={String(report.guests)} />
        <Tile label={t('analytics.tileNew')} value={String(report.new_guests)} />
        <Tile label={t('analytics.tileReturning')} value={String(report.returning_guests)} />
        <Tile
          label={t('analytics.tileRepeatRate')}
          value={report.repeat_rate === null ? '—' : `${Math.round(report.repeat_rate * 100)}%`}
        />
      </div>
      <div className="table-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th scope="col">{t('analytics.colGuest')}</th>
              <th scope="col">{t('analytics.colVisits')}</th>
              <th scope="col">{t('analytics.colLifetime')}</th>
              <th scope="col">{t('analytics.colFirstSeen')}</th>
              <th scope="col">{t('analytics.colLastSeen')}</th>
            </tr>
          </thead>
          <tbody>
            {report.top_guests.map((guest) => (
              <tr key={guest.client_id}>
                <th scope="row">{guest.display_name}</th>
                <td>{guest.visits}</td>
                {/* Lifetime, not the window: what this person has been worth in total. */}
                <td>{formatMoney(guest.lifetime_cents, currency, locale)}</td>
                <td>{guest.first_seen_on ?? '—'}</td>
                <td>{guest.last_seen_on ?? '—'}</td>
              </tr>
            ))}
            {report.top_guests.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted">
                  {t('reports.nobodyYet')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Tile({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="tile">
      <span className="tile__value">{value}</span>
      <span className="tile__label">{label}</span>
    </div>
  );
}
